import { useEffect } from 'react';

import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useInfoStore } from 'store/useInfoStore';

import { Pageable, UploadReviewRequest } from '../model/dto';
import { BillingCardOrderType, CheckServicePlanType, CouponTargetService, ReviewType } from '../types';
import { ApiError } from './customError';
import {
  getAllReviews,
  getBestReviews,
  getBillingCards,
  getCheckService,
  getMemberDetail,
  getReviewsByType,
  uploadReview,
  uploadReviewImages,
  validateCoupon,
} from './service';
import { checkBillingCards, getReviewDetail } from './service';

export const useUploadReviewImages = (images: File[]) => {
  return useMutation({
    mutationFn: () => uploadReviewImages(images),
  });
};

export const useUploadReview = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError?: (message: string) => void;
}) => {
  return useMutation({
    mutationFn: (request: UploadReviewRequest) => uploadReview(request),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        onError?.(error.message);
      }
    },
  });
};

export const useGetAllReviewsInfinite = (type: ReviewType, pageSize: number) => {
  return useInfiniteQuery({
    queryKey: ['allReviews', type, pageSize],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getAllReviews({ type, pageable: { page: pageParam, size: pageSize } }),

    getNextPageParam: (lastPage) => {
      const next = lastPage.pageInfo.pageNum + 1;
      return next < lastPage.pageInfo.totalPages ? next : undefined;
    },
  });
};

export const MEMBER_DETAIL_QUERY_KEY = ['member', 'detail'];

export const useGetMemberDetail = (enabled: boolean) => {
  const { userId, isLogin } = useInfoStore();
  const isAuthenticated = isLogin && !!userId;
  const query = useQuery({
    // 회원이 바뀌면 이전 회원의 캐시나 늦게 도착한 응답을 재사용하지 않는다.
    queryKey: [...MEMBER_DETAIL_QUERY_KEY, userId],
    queryFn: () => getMemberDetail(),
    enabled: enabled && isAuthenticated,
    gcTime: 0,
  });
  useEffect(() => {
    if (isAuthenticated && query.data?.profileCompleted !== undefined) {
      useInfoStore.getState().setProfileCompleted(query.data.profileCompleted);
    }
  }, [isAuthenticated, query.data]);
  return { ...query, data: isAuthenticated ? query.data : undefined };
};

export const useGetCheckService = (serviceType: CheckServicePlanType) => {
  return useQuery({
    queryKey: ['checkService', serviceType],
    queryFn: () => getCheckService(serviceType),
  });
};

export const useValidateCoupon = ({
  code,
  targetAmount,
  couponTargetService,
}: {
  code: string;
  targetAmount: number;
  couponTargetService: CouponTargetService;
}) => {
  return useQuery({
    queryKey: ['coupon', code, targetAmount, couponTargetService],

    queryFn: async () => {
      return validateCoupon(code, targetAmount, couponTargetService);
    },
    enabled: false,
  });
};

export const useGetReviewsByType = (type: ReviewType, pageable: Pageable) => {
  return useQuery({
    queryKey: ['REVIEWS_BY_TYPE', type, ...Object.values(pageable)],
    queryFn: () => getReviewsByType({ type, pageable }),
  });
};

export const useGetBestReviews = (type: ReviewType, pageable: Pageable) => {
  return useQuery({
    queryKey: ['BEST_REVIEWS', type, ...Object.values(pageable)],
    queryFn: () => getBestReviews({ type, pageable }),
  });
};

export const useGetReviewDetail = (id: number) => {
  return useQuery({
    queryKey: ['REVIEW_DETAIL', id],
    queryFn: () => getReviewDetail(id),
  });
};

export const useCheckBillingCards = (orderType?: BillingCardOrderType) => {
  return useQuery({
    queryKey: ['check-billing-cards', orderType],
    queryFn: () => checkBillingCards(orderType),
  });
};

export const useGetBillingCards = (memberId: string, orderType?: BillingCardOrderType) => {
  return useQuery({
    queryKey: ['billing-cards', memberId, orderType],
    queryFn: () => getBillingCards(memberId, orderType),
  });
};
